import { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Package, Heart, ShoppingCart, Star, Truck, Zap } from 'lucide-react';
import { IMAGE_BASE } from '../services/api';

export default function ProductCard({
    product,
    formatPrice,
    addingId,
    handleAddToCart,
    isFavorited,
    toggleFavorite,
    showDescription = false,
    showShippingBadges = false,
    showRating = true,
    showVariantColors = true,
    discountLabel = false,
    variantDiscountOnly = false,
}) {
    const [hoverImgIdx, setHoverImgIdx] = useState(null);
    const [showVideo, setShowVideo] = useState(false);
    const intervalRef = useRef(null);
    const videoRef = useRef(null);
    const cardRef = useRef(null);

    const allMedia = product.images || [];
    const images = allMedia.filter(i => i.mediaType !== 'video');
    const video = allMedia.find(i => i.mediaType === 'video');
    const primaryImage = images.find(i => i.isPrimary) || images[0];
    const displayImage = hoverImgIdx !== null ? images[hoverImgIdx] : primaryImage;

    const getUrl = (item) => {
        if (!item) return null;
        return item.url.startsWith('http') ? item.url : IMAGE_BASE + item.url;
    };

    const effectiveStock = product.hasVariants
        ? (product.totalVariantStock ?? product.stock)
        : (product.stock ?? 0);

    const handleMouseEnter = useCallback(() => {
        if (video) {
            setShowVideo(true);
        } else if (images.length > 1) {
            const primaryIdx = primaryImage ? images.indexOf(primaryImage) : 0;
            let idx = (primaryIdx + 1) % images.length;
            setHoverImgIdx(idx);
            intervalRef.current = setInterval(() => {
                setHoverImgIdx(prev => ((prev ?? 0) + 1) % images.length);
            }, 1200);
        }
    }, [video, images, primaryImage]);

    const handleMouseLeave = useCallback(() => {
        setShowVideo(false);
        if (videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setHoverImgIdx(null);
    }, []);

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const imgUrl = getUrl(displayImage);

    const showDiscount = variantDiscountOnly
        ? (!product.hasVariants && product.comparePrice && product.price < product.comparePrice)
        : (product.comparePrice && product.price < product.comparePrice);

    const discountPct = showDiscount
        ? Math.round((1 - product.price / product.comparePrice) * 100)
        : 0;

    return (
        <Link
            ref={cardRef}
            to={`/urun/${product.slug}`}
            className={`product-card${effectiveStock <= 0 ? ' product-card-sold-out' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {/* Image Frame */}
            <div className="product-card-frame">
                <div className="product-card-image">
                    {showVideo && video ? (
                        <video
                            ref={videoRef}
                            src={getUrl(video)}
                            autoPlay muted loop playsInline
                            className="product-card-video"
                        />
                    ) : imgUrl ? (
                        <img src={imgUrl} alt={product.name} loading="lazy" />
                    ) : (
                        <div className="product-card-placeholder"><Package size={32} strokeWidth={1} /></div>
                    )}

                    {/* Image dots */}
                    {!showVideo && images.length > 1 && hoverImgIdx !== null && (
                        <div className="product-card-img-dots">
                            {images.map((_, i) => (
                                <span key={i} className={`product-card-img-dot${i === hoverImgIdx ? ' active' : ''}`} />
                            ))}
                        </div>
                    )}

                    {/* Hover overlay */}
                    <div className="product-card-overlay">
                        <span className="product-card-view">Tabloyu İncele</span>
                    </div>
                </div>

                {/* Top badges */}
                {effectiveStock <= 0 ? (
                    <span className="product-card-badge badge-sold-out">Tükendi</span>
                ) : effectiveStock < 5 ? (
                    <span className="product-card-badge badge-low-stock">Son Birkaç Adet</span>
                ) : null}

                {showDiscount && (
                    <span className="product-card-badge badge-discount">
                        %{discountPct}{discountLabel ? ' İNDİRİM' : ''}
                    </span>
                )}

                {/* Favorite button */}
                {isFavorited && toggleFavorite && (
                    <button
                        className={`fav-card-btn ${isFavorited(product.id) ? 'fav-card-btn-active' : ''}`}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(product.id); }}
                        title={isFavorited(product.id) ? 'Favorilerden çıkar' : 'Favorilere ekle'}
                    >
                        <Heart size={16} fill={isFavorited(product.id) ? 'currentColor' : 'none'} />
                    </button>
                )}
            </div>

            {/* Info */}
            <div className="product-card-body">
                <span className="product-card-category">{product.category?.name}</span>
                <h3 className="product-card-title">{product.name}</h3>

                {showDescription && product.shortDescription && (
                    <p className="product-card-desc">{product.shortDescription}</p>
                )}

                {showRating && product.reviewCount > 0 && (
                    <div className="product-card-rating">
                        <div className="product-card-stars">
                            {[1, 2, 3, 4, 5].map(s => (
                                <Star key={s} size={12} fill={s <= Math.round(product.avgRating) ? '#c8a97e' : 'none'} color={s <= Math.round(product.avgRating) ? '#c8a97e' : '#d4d4d4'} />
                            ))}
                        </div>
                        <span className="product-card-rating-text">({product.reviewCount})</span>
                    </div>
                )}

                {showShippingBadges && (product.hasFreeShipping || product.hasFastDelivery) && (
                    <div className="product-card-shipping-tags">
                        {product.hasFreeShipping && <span className="ship-tag"><Truck size={11} /> Ücretsiz Kargo</span>}
                        {product.hasFastDelivery && <span className="ship-tag ship-tag-fast"><Zap size={11} /> Hızlı</span>}
                    </div>
                )}

                <div className="product-card-footer">
                    <div className="product-card-price">
                        {product.hasVariants && product.displayPrice ? (
                            <span className="price-current">{formatPrice(product.displayPrice)}<small>'den</small></span>
                        ) : (
                            <span className="price-current">{formatPrice(product.price)}</span>
                        )}
                        {!product.hasVariants && product.comparePrice && product.price < product.comparePrice && (
                            <span className="price-compare">{formatPrice(product.comparePrice)}</span>
                        )}
                    </div>
                    {handleAddToCart && (
                        <button
                            className="product-card-add-btn"
                            disabled={addingId === product.id || effectiveStock <= 0}
                            onClick={(e) => handleAddToCart(e, product)}
                        >
                            <ShoppingCart size={14} />
                        </button>
                    )}
                </div>
            </div>
        </Link>
    );
}
